import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

const CODE_LENGTH = 6;
const INPUT_GAP = 3;
const PRIMARY_BUTTON_SX = {
  bgcolor: '#0046fe',
  '&:hover': { bgcolor: '#0039cc' },
};
const FONT_ROBOTO = "'Roboto', sans-serif";

export type VerificationCodeErrorReason =
  | 'expired'
  | 'invalid'
  | 'max_attempts';

export interface VerificationCodeInputProps {
  /** Called when the user clicks validate with a complete 6-digit code */
  onValidate: (code: string) => void;
  /** When true, the validate button shows loading and is disabled */
  isValidating?: boolean;
  /** When true, an error message is shown below the button */
  error?: boolean;
  /** When error is true, optional reason to show a specific message (expired, max_attempts, or generic invalid) */
  errorReason?: VerificationCodeErrorReason;
  /** Optional: when provided, a "resend code" link is shown below the button */
  onResend?: () => void;
  /** When true (and onResend is set), the resend link shows loading state */
  isSending?: boolean;
  /** When > 0, the resend link is disabled and shows a countdown (seconds) */
  resendCooldownSeconds?: number;
}

export function VerificationCodeInput({
  onValidate,
  isValidating = false,
  error = false,
  errorReason,
  onResend,
  isSending = false,
  resendCooldownSeconds = 0,
}: VerificationCodeInputProps) {
  const resendDisabled = isSending || resendCooldownSeconds > 0;
  const intl = useIntl();
  const [codeDigits, setCodeDigits] = useState<string[]>(() =>
    Array.from<string>({ length: CODE_LENGTH }).fill(''),
  );
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const setCodeDigit = useCallback((index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    setCodeDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }, []);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent, startIndex: number) => {
      e.preventDefault();
      const digitsOnly = e.clipboardData.getData('text').replace(/\D/g, '');
      const pasted = digitsOnly.slice(0, CODE_LENGTH).split('');

      if (pasted.length === CODE_LENGTH) {
        setCodeDigits(pasted);
        inputRefs.current[CODE_LENGTH - 1]?.focus();
      } else {
        setCodeDigits((prev) => {
          const next = [...prev];
          pasted.forEach((char, i) => {
            if (startIndex + i < CODE_LENGTH) next[startIndex + i] = char;
          });
          return next;
        });
        const nextFocus = Math.min(startIndex + pasted.length, CODE_LENGTH - 1);
        inputRefs.current[nextFocus]?.focus();
      }
    },
    [],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
        setCodeDigits((prev) => {
          const next = [...prev];
          next[index - 1] = '';
          return next;
        });
      }
      if (e.key === 'ArrowLeft' && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
      if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [codeDigits],
  );

  const fullCode = codeDigits.join('');
  const canValidate = fullCode.length === CODE_LENGTH;

  const handleValidateClick = useCallback(() => {
    if (!canValidate || isValidating) return;
    onValidate(fullCode);
  }, [fullCode, canValidate, isValidating, onValidate]);

  const handleResendClick = useCallback(() => {
    setCodeDigits(Array.from<string>({ length: CODE_LENGTH }).fill(''));
    onResend?.();
  }, [onResend]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: INPUT_GAP,
          justifyContent: 'center',
          mb: 2,
        }}
      >
        {Array.from({ length: CODE_LENGTH }, (_, i) => (
          <TextField
            key={i}
            inputRef={(el: HTMLInputElement | null) => {
              inputRefs.current[i] = el;
            }}
            value={codeDigits[i]}
            onChange={(e) => setCodeDigit(i, e.target.value)}
            onPaste={(e) => handlePaste(e, i)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            inputProps={{
              maxLength: 1,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              'aria-label': intl.formatMessage(
                { id: 'invitation.codeDigitAria' },
                { index: i + 1 },
              ),
            }}
            sx={{
              width: 48,
              '& .MuiInputBase-input': {
                textAlign: 'center',
                fontSize: '1.25rem',
              },
            }}
          />
        ))}
      </Box>
      <Button
        variant="contained"
        onClick={handleValidateClick}
        disabled={!canValidate || isValidating}
        sx={PRIMARY_BUTTON_SX}
        fullWidth
      >
        {isValidating ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
            {intl.formatMessage({ id: 'invitation.validatingCode' })}
          </>
        ) : (
          intl.formatMessage({ id: 'invitation.validateCodeButton' })
        )}
      </Button>
      {onResend != null && (
        <Typography
          component="button"
          type="button"
          variant="body2"
          onClick={handleResendClick}
          disabled={resendDisabled}
          sx={{
            display: 'block',
            mt: 1.5,
            mx: 'auto',
            border: 'none',
            background: 'none',
            cursor: resendDisabled ? 'default' : 'pointer',
            color: resendDisabled ? 'text.disabled' : '#0046fe',
            fontFamily: FONT_ROBOTO,
            textDecoration: 'none',
          }}
        >
          {isSending
            ? intl.formatMessage({ id: 'invitation.sendingCode' })
            : resendCooldownSeconds > 0
              ? intl.formatMessage(
                  { id: 'invitation.resendCodeWithCooldown' },
                  { seconds: resendCooldownSeconds },
                )
              : intl.formatMessage({ id: 'invitation.resendCodeLink' })}
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorReason === 'expired'
            ? intl.formatMessage({ id: 'invitation.codeErrorExpired' })
            : errorReason === 'max_attempts'
              ? intl.formatMessage({ id: 'invitation.codeErrorMaxAttempts' })
              : intl.formatMessage({ id: 'invitation.codeError' })}
        </Alert>
      )}
    </>
  );
}
